/* eslint-disable max-len */
import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';

import { Select } from 'uikit/selects';
import { Typo } from 'uikit/content';
import { Checkbox, RadioInput } from 'uikit/inputs';

import getFrequencyOptions from '../helpers/getFrequencyOptions';
import getIntervalOptions from '../helpers/getIntervalOptions';
import getDaysOptions from '../helpers/getDaysOptions';
import getByOptions from '../helpers/getByOptions';

import DatePicker from '../../exceed/components/datePicker';

import styles from '../styles/formFieldEventRepeat.module.scss';

const Frequency = ({ handleChange, isDisabled, value }) => (
  <div className={styles.field}>
    <Select
      className={styles.frequency}
      id="form-field-event-repeat-frequency"
      isDisabled={isDisabled}
      options={getFrequencyOptions()}
      onChange={({ value: selectValue }) =>
        handleChange({ key: 'frequency', updateValue: selectValue })
      }
      value={getFrequencyOptions().find((option) => value === option.value)}
      isSearchable={false}
    />
  </div>
);

const Interval = ({ handleChange, value, frequency }) => {
  const labelMapping = {
    daily: i18n.t('days'),
    weekly: i18n.t('weeks'),
    monthly: i18n.t('months'),
  };

  const options = getIntervalOptions({ type: 'days', frequency });

  return (
    <div className={styles.intervalField}>
      <Typo className={styles.fieldLabel} variant="s03" weight="medium">
        {i18n.t('repeat-every')}
      </Typo>
      <div className={styles.intervalContainer}>
        <Select
          className={styles.interval}
          id="form-field-event-repeat-interval"
          options={options}
          onChange={(selectedOption) =>
            handleChange({ key: 'interval', updateValue: selectedOption.value })
          }
          value={options.find((item) => item.value === value)}
          isSearchable={false}
        />
        <Typo variant="s02">{labelMapping[frequency]}</Typo>
      </div>
    </div>
  );
};

const Days = ({ value: selectedDays, handleChange, fieldId }) =>
  getDaysOptions().map(({ label, value }) => (
    <Checkbox
      key={`${fieldId}-${value}`}
      id={`${fieldId}-${value}`}
      className={styles.days}
      onChange={(isChecked) => handleChange({ key: 'days', updateValue: { [value]: isChecked } })}
      label={label}
      value={selectedDays[value]}
    />
  ));

const By = ({ value, handleChange, fieldId }) => (
  <div className={styles.by}>
    {getByOptions().map(({ label, value: radioValue }, index) => (
      <RadioInput
        key={`${fieldId}-${label}`}
        className={styles.by}
        labelPosition="right"
        fieldId={`${fieldId}-${index}`}
        value={value === radioValue}
        label={label}
        updateField={() => handleChange({ key: 'by', updateValue: radioValue })}
        options={null}
      />
    ))}
  </div>
);

const Until = ({ value, handleChange, fieldId }) => (
  <div className={styles.until}>
    <Typo variant="s03" weight="medium">
      {i18n.t('repeat-until')}
    </Typo>
    <Typo color="secondary" variant="b01" className={styles.fieldHelp}>
      {i18n.t('repeat-until-help')}
    </Typo>
    <DatePicker
      id={`${fieldId}-date-picker`}
      value={value}
      onChange={(date) => handleChange({ key: 'until', updateValue: date })}
      inputType="button"
      placeholder={i18n.t('select-date')}
    />
  </div>
);

const FormFieldEventRepeat = ({
  fieldId,
  handleChange = () => {},
  model,
  schema: { isDisabled },
}) => {
  const { frequency, interval, by, days, until } = model;

  const isFrequencySelected = frequency;
  const isWeeklyFrequencySelected = frequency === 'weekly';
  const isMonthlyFrequencySelected = frequency === 'monthly';

  return (
    <>
      <Frequency handleChange={handleChange} isDisabled={isDisabled} value={frequency} />
      {isFrequencySelected && (
        <Interval value={interval} handleChange={handleChange} frequency={frequency} />
      )}
      {isWeeklyFrequencySelected && (
        <Days value={days} handleChange={handleChange} fieldId={fieldId} />
      )}
      {isMonthlyFrequencySelected && (
        <By value={by} handleChange={handleChange} fieldId={fieldId} />
      )}
      {isFrequencySelected && <Until value={until} handleChange={handleChange} fieldId={fieldId} />}
    </>
  );
};

FormFieldEventRepeat.propTypes = {
  model: PropTypes.shape({}).isRequired,
  handleChange: PropTypes.func,
  schema: PropTypes.shape({ isDisabled: PropTypes.bool }).isRequired,
};

export default React.memo(FormFieldEventRepeat);
